import type { EventCheckinCodeResponse } from '@pulsify/shared/dto'
import { Download, Printer, Sparkles } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import {
  type CSSProperties,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useShareLinks } from '~/entities/share-link/model/use-share-links'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import type { Event } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import { CheckinFlyer } from './checkin-flyer'
import {
  buildFlyerContent,
  type FlyerContent,
  type FlyerFormat,
  type FlyerLocale,
  type FlyerMotif,
  PAPER_SIZES_MM,
  qrShareUrl,
} from './flyer-content'
import { PrintFlyer } from './print-flyer'
import { renderStoryImage } from './story-image'

const STORY_QR_SIZE = 1024
const EVENT_MOTIFS: FlyerMotif[] = ['onsite', 'queue', 'ahead', 'promo']

type BuildFlyerContent = (motif: FlyerMotif, locale: FlyerLocale) => FlyerContent

type StoryState =
  | { status: 'idle' }
  | { status: 'rendering' }
  | { status: 'ready'; url: string }
  | { status: 'failed' }

export function CheckinFlyerDialog({
  businessId,
  event,
  checkin,
  compact,
}: {
  businessId: string
  event: Event
  checkin: EventCheckinCodeResponse
  compact?: boolean
}) {
  const shareLinks = useShareLinks(businessId, { kind: 'event', id: event.id })
  const promoUrl = qrShareUrl(shareLinks.data)
  const buildContent = useCallback<BuildFlyerContent>(
    (motif, locale) => buildFlyerContent(event, checkin, motif, locale, promoUrl),
    [event, checkin, promoUrl],
  )
  return <FlyerDialog motifs={EVENT_MOTIFS} buildContent={buildContent} compact={compact} />
}

/** The flyer and story maker for any Live Room code; the caller supplies the words. */
export function FlyerDialog({
  motifs,
  buildContent,
  compact,
}: {
  motifs: FlyerMotif[]
  buildContent: BuildFlyerContent
  compact?: boolean
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {compact ? (
          <Button size="sm" className="w-full" aria-label={m.checkinFlyerCreate()}>
            <Sparkles />
            {m.checkinFlyerShort()}
          </Button>
        ) : (
          <Button className="w-full">
            <Sparkles className="mr-2 h-4 w-4" />
            {m.checkinFlyerCreate()}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{m.checkinFlyerTitle()}</DialogTitle>
          <DialogDescription>{m.checkinFlyerDescription()}</DialogDescription>
        </DialogHeader>
        <FlyerWorkspace motifs={motifs} buildContent={buildContent} />
      </DialogContent>
    </Dialog>
  )
}

function FlyerWorkspace({
  motifs,
  buildContent,
}: {
  motifs: FlyerMotif[]
  buildContent: BuildFlyerContent
}) {
  const [format, setFormat] = useState<FlyerFormat>('a5')
  const [motif, setMotif] = useState<FlyerMotif>(motifs[0] ?? 'onsite')
  const [locale, setLocale] = useState<FlyerLocale>(getLocale() === 'en' ? 'en' : 'de')
  const content = useMemo(
    () => buildContent(format === 'story' && motif !== 'promo' ? 'ahead' : motif, locale),
    [buildContent, format, motif, locale],
  )
  const [story, setStory] = useState<StoryState>({ status: 'idle' })
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const storyUrlRef = useRef<string | null>(null)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      if (storyUrlRef.current) URL.revokeObjectURL(storyUrlRef.current)
      storyUrlRef.current = null
    }
  }, [])

  const renderStory = async () => {
    const qrCanvas = qrCanvasRef.current
    if (!qrCanvas) return
    setStory({ status: 'rendering' })
    try {
      const blob = await renderStoryImage(content, qrCanvas)
      if (!aliveRef.current) return
      const url = URL.createObjectURL(blob)
      storyUrlRef.current = url
      setStory({ status: 'ready', url })
    } catch {
      if (aliveRef.current) setStory({ status: 'failed' })
    }
  }

  const selectFormat = (next: FlyerFormat) => {
    setFormat(next)
    if (next === 'story') setStory({ status: 'idle' })
  }

  const selectLocale = (next: FlyerLocale) => {
    setLocale(next)
    if (format === 'story') setStory({ status: 'idle' })
  }

  useEffect(() => {
    if (format === 'story' && story.status === 'idle') renderStory()
  })

  const handlePrint = async () => {
    const images = Array.from(sheetRef.current?.querySelectorAll('img') ?? [])
    await Promise.all(images.map((image) => image.decode().catch(() => undefined)))
    window.print()
  }

  const handleDownloadStory = () => {
    if (story.status !== 'ready') return
    const link = document.createElement('a')
    link.href = story.url
    link.download = `pulsify-story-${content.code}.png`
    link.click()
  }

  return (
    <div className="flex flex-col gap-4">
      <SegmentedControl
        label={m.checkinFlyerFormat()}
        value={format}
        onChange={selectFormat}
        options={[
          { value: 'a5', label: m.checkinFlyerFormatA5() },
          { value: 'a4', label: m.checkinFlyerFormatA4() },
          { value: 'white', label: m.checkinFlyerFormatWhite() },
          { value: 'story', label: m.checkinFlyerFormatStory() },
        ]}
      />
      <div className="flex flex-wrap gap-4">
        {format === 'story' || motifs.length < 2 ? null : (
          <SegmentedControl
            label={m.checkinFlyerMotif()}
            value={motif}
            onChange={setMotif}
            options={motifs.map((value) => ({ value, label: MOTIF_LABELS[value]() }))}
          />
        )}
        <SegmentedControl
          label={m.checkinFlyerLanguage()}
          value={locale}
          onChange={selectLocale}
          options={[
            { value: 'de', label: 'Deutsch' },
            { value: 'en', label: 'English' },
          ]}
        />
      </div>

      <section
        aria-label={m.checkinFlyerPreviewLabel()}
        className="flex justify-center rounded-lg bg-muted p-4"
      >
        {format === 'story' ? (
          <StoryPreview story={story} title={content.title} />
        ) : (
          <FlyerSheet
            content={content}
            format={format}
            className="aspect-[148/210] w-full max-w-80 rounded-md shadow-lg"
          />
        )}
      </section>

      {story.status === 'failed' && format === 'story' && (
        <p className="text-sm text-destructive">{m.checkinFlyerStoryFailed()}</p>
      )}

      <DialogFooter>
        {format === 'story' ? (
          <Button
            onClick={story.status === 'failed' ? renderStory : handleDownloadStory}
            disabled={story.status === 'rendering' || story.status === 'idle'}
          >
            <Download className="mr-2 h-4 w-4" />
            {story.status === 'rendering'
              ? m.checkinFlyerRendering()
              : story.status === 'failed'
                ? m.tryAgain()
                : m.checkinFlyerDownloadStory()}
          </Button>
        ) : (
          <Button onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            {m.checkinQrPrint()}
          </Button>
        )}
      </DialogFooter>

      <QRCodeCanvas
        ref={qrCanvasRef}
        value={content.url}
        size={STORY_QR_SIZE}
        marginSize={0}
        className="hidden"
        aria-hidden
      />
      {format !== 'story' && <PrintSheet ref={sheetRef} content={content} format={format} />}
    </div>
  )
}

const MOTIF_LABELS: Record<FlyerMotif, () => string> = {
  onsite: m.checkinFlyerMotifOnsite,
  queue: m.checkinFlyerMotifQueue,
  ahead: m.checkinFlyerMotifAhead,
  promo: m.checkinFlyerMotifPromo,
}

function StoryPreview({ story, title }: { story: StoryState; title: string }) {
  if (story.status === 'ready') {
    return (
      <img
        src={story.url}
        alt={m.checkinQrImageAlt({ eventTitle: title })}
        className="aspect-[9/16] w-full max-w-60 rounded-md shadow-lg"
      />
    )
  }
  return <Skeleton className="aspect-[9/16] w-full max-w-60 rounded-md" />
}

function PrintSheet({
  content,
  format,
  ref,
}: {
  content: FlyerContent
  format: Exclude<FlyerFormat, 'story'>
  ref: Ref<HTMLDivElement>
}) {
  const paper = PAPER_SIZES_MM[format]
  return createPortal(
    <div ref={ref} data-print-sheet className="hidden print:block">
      <style>{`@page { size: ${paper.width}mm ${paper.height}mm; margin: 0; }`}</style>
      <FlyerSheet
        content={content}
        format={format}
        className="break-after-avoid"
        style={{ width: `${paper.width}mm`, height: `${paper.height}mm` }}
      />
    </div>,
    document.body,
  )
}

function FlyerSheet({
  format,
  ...props
}: {
  content: FlyerContent
  format: Exclude<FlyerFormat, 'story'>
  className: string
  style?: CSSProperties
}) {
  return format === 'white' ? <PrintFlyer {...props} /> : <CheckinFlyer {...props} />
}
